import { CaretDown, CaretUp } from '@liveauctioneers/hammer-ui-core/icons';
import { FormattedNumber } from '@liveauctioneers/hammer-ui-core/intl';
import { BodyPrimary, H2 } from '@liveauctioneers/hammer-ui-core/text';
import { useState } from 'react';
import { Collapse } from 'react-collapse';

import { RouteButton } from '@/components/RouteButton/RouteButton';
import { RouteLink } from '@/components/RouteLinks/RouteLinks';
import { buildBrowseCategoryPath } from '@/utils/browsePath/browsePath';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { addImageParams, AddImageParamTypes } from '@/utils/urls';

import { Category } from '../../types/CategoryTypes';
import { urlToSend } from '../utils/urlToSend';

export type DropDownProps = {
    categories: Category[];
    index: number;
    title: string;
};

export const DropDown = ({ categories, index, title }: DropDownProps) => {
    const [show, setShow] = useState(false);
    const formatMessage = useAppFormatMessage();
    return (
        <div>
            {index === 0 && <div className="border border-border-secondary" />}
            <div
                className="flex flex-row justify-between"
                onClick={() => setShow(!show)}
            >
                <H2 className="my-md">
                    {formatMessage(['discoverPage', 'subCategory', 'subCategoryTitle'], { values: { title } })}
                </H2>
                <div className="flex items-center">
                    {show ? (
                        <CaretUp
                            size="xs"
                            style="fill"
                        />
                    ) : (
                        <CaretDown
                            size="xs"
                            style="fill"
                        />
                    )}
                </div>
            </div>
            <Collapse isOpened={show}>
                <div className="max-[540px]:justify-around flex flex-wrap">
                    {categories.map((category) => {
                        const imageParams: AddImageParamTypes = {
                            height: 157,
                            url: category.imageUrl,
                        };

                        return (
                            <div
                                className="mr-xs w-[157px]"
                                key={category.title}
                            >
                                <RouteLink
                                    className="block h-full w-full no-underline"
                                    to={category.pageUrl}
                                >
                                    <div className="relative flex h-[157px] w-[157px] items-center justify-center rounded-sm">
                                        <img
                                            alt={category.title}
                                            className="max-h-[157px] max-w-[157px] rounded-[7px] object-cover py-2xs"
                                            loading="lazy"
                                            src={addImageParams(imageParams)}
                                        />
                                    </div>
                                    <div className="flex flex-col py-xs [&:active_span]:text-primary-400 [&:active_span]:underline">
                                        <BodyPrimary>
                                            {formatMessage(['discoverPage', 'subCategory', 'subCategoryTitleSpan'], {
                                                values: { title: category.title },
                                            })}
                                        </BodyPrimary>
                                        {category.followers > 0 && (
                                            <BodyPrimary color="secondary">
                                                {category.followers === 1
                                                    ? formatMessage([
                                                          'discoverPage',
                                                          'subCategory',
                                                          'subCategoryFollower',
                                                      ])
                                                    : formatMessage(
                                                          ['discoverPage', 'subCategory', 'subCategoryFollowers'],
                                                          {
                                                              values: {
                                                                  followers: (
                                                                      <FormattedNumber
                                                                          key="followers"
                                                                          value={category.followers}
                                                                      />
                                                                  ),
                                                              },
                                                          }
                                                      )}
                                            </BodyPrimary>
                                        )}
                                    </div>
                                </RouteLink>
                            </div>
                        );
                    })}
                </div>
                <div className="my-lg flex items-center justify-center">
                    <RouteButton
                        className="w-fit"
                        size="sm"
                        suppressHydrationWarning
                        to={buildBrowseCategoryPath(urlToSend(title.toLocaleLowerCase()), [])}
                    >
                        {formatMessage(['discoverPage', 'subCategory', 'subCategoryLink'], { values: { title } })}
                    </RouteButton>
                </div>
            </Collapse>
            <div className="border border-border-secondary" />
        </div>
    );
};
